import {
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Select,
	SelectContent,
	SelectItem,
	SelectSeparator,
	SelectTrigger,
	SelectValue,
	Textarea,
} from "@/components/primitives";
import {
	ArrowLeft,
	Bookmark,
	BookOpen,
	Calendar,
	CheckCircle,
	TrendingUp,
} from "lucide-react";
import { createBookmark, getFeelingText } from "../utils";
import { getFeelingOptionWithArrows } from "@/utils/feeling-utils";
import type { Reflection, ReflectionResponse } from "../types";
import { ReflectionDetailsSection } from "./ReflectionDetailsSection";

interface CheckInFormProps {
	generalFeeling: number;
	setGeneralFeeling: (value: number) => void;
	dayDescription: string;
	setDayDescription: (value: string) => void;
	reflections: Reflection[];
	selectedReflections: string | null;
	setSelectedReflections: (value: string | null) => void;
	reflectionResponses: Record<string, ReflectionResponse>;
	setReflectionResponses: (
		value:
			| Record<string, ReflectionResponse>
			| ((
					prev: Record<string, ReflectionResponse>,
			  ) => Record<string, ReflectionResponse>),
	) => void;
	onBack: () => void;
	onSubmit: () => void;
	submitting: boolean;
	isFormValid: boolean;
}

export function CheckInForm({
	generalFeeling,
	setGeneralFeeling,
	dayDescription,
	setDayDescription,
	reflections,
	selectedReflections,
	setSelectedReflections,
	reflectionResponses,
	setReflectionResponses,
	onBack,
	onSubmit,
	submitting,
	isFormValid,
}: CheckInFormProps) {
	const handleReflectionSelect = (reflectionId: string) => {
		// If clicking the same reflection, deselect it
		if (selectedReflections === reflectionId) {
			setSelectedReflections(null);
			setReflectionResponses((prev) => {
				const copy = { ...prev };
				delete copy[reflectionId];
				return copy;
			});
		} else {
			// Select the new reflection
			setSelectedReflections(reflectionId);
			setReflectionResponses((prev) => {
				const updated = { ...prev };
				// Initialize new reflection's response if it doesn't exist
				if (!updated[reflectionId]) {
					updated[reflectionId] = { impact: null, reasons: "", signals: [] };
				}
				return updated;
			});
		}
	};

	return (
		<div className="max-w-4xl mx-auto">
			<Card className="border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm">
				<CardHeader className="pb-6 border-b bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
					<div className="flex items-center justify-between gap-4">
						<div className="flex items-center gap-3">
							<div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
								<Calendar className="h-5 w-5 text-white" />
							</div>
							<CardTitle className="text-2xl font-bold text-foreground">
								Your Daily Reflection
							</CardTitle>
						</div>
						<div className="flex items-center gap-2 flex-shrink-0">
							<Button
								variant="ghost"
								onClick={onBack}
								className="flex items-center"
							>
								<ArrowLeft className="h-4 w-4 mr-2" />
								Back
							</Button>
							<Button
								variant="ghost"
								onClick={createBookmark}
								title="Create bookmark"
								className="flex-shrink-0"
							>
								<Bookmark className="h-4 w-4" />
							</Button>
						</div>
					</div>
				</CardHeader>
				<CardContent className="space-y-8 pt-8">
					<div className="space-y-8">
						{/* How are you feeling section */}
						<div className="space-y-6">
							<div className="flex items-center gap-3 pb-3 border-b">
								<div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
									<TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
								</div>
								<h3 className="text-xl font-semibold text-foreground">
									How are you feeling today?
								</h3>
							</div>

							<div className="space-y-6 pl-2">
								<div className="space-y-2">
									<Select
										value={
											generalFeeling !== 0
												? generalFeeling.toString()
												: undefined
										}
										onValueChange={(value) => setGeneralFeeling(Number(value))}
									>
										<SelectTrigger className="h-12 text-base">
											<SelectValue
												placeholder={getFeelingText(generalFeeling)}
											/>
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="3">
												{getFeelingOptionWithArrows(
													3,
													"Much better than my last daily reflection",
												)}
											</SelectItem>
											<SelectItem value="1">
												{getFeelingOptionWithArrows(
													1,
													"Slightly better than my last daily reflection",
												)}
											</SelectItem>
											<SelectSeparator />
											<SelectItem value="-1">
												{getFeelingOptionWithArrows(
													-1,
													"Slightly worse than my last daily reflection",
												)}
											</SelectItem>
											<SelectItem value="-3">
												{getFeelingOptionWithArrows(
													-3,
													"Much worse than my last daily reflection",
												)}
											</SelectItem>
										</SelectContent>
									</Select>
								</div>

								<div className="space-y-2">
									<label className="block text-base font-semibold text-foreground mb-3">
										How would you describe your day?
									</label>
									<Textarea
										value={dayDescription}
										onChange={(e) => setDayDescription(e.target.value)}
										placeholder="Take a moment to reflect on your day. What happened? How did you feel? What stood out to you? This is your space to capture the essence of today..."
										className="min-h-[140px] text-base leading-relaxed resize-y"
									/>
									<p className="text-xs text-muted-foreground mt-1">
										Write as much or as little as feels right. There's no right
										or wrong way to reflect.
									</p>
								</div>
							</div>
						</div>

						{reflections.length > 0 && (
							<div className="pt-4 border-t">
								<div className="flex items-center gap-3 mb-4">
									<div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
										<BookOpen className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
									</div>
									<label className="block text-base font-semibold text-foreground">
										Would you like to reflect on any of your active reflections?
									</label>
								</div>
								<div className="space-y-3 pl-2">
									<label
										className={`flex items-start space-x-3 p-3 rounded-lg border-2 transition-all cursor-pointer ${
											selectedReflections === null
												? "border-indigo-500 bg-indigo-50 dark:bg-indigo-950/20"
												: "border-border hover:border-indigo-300 hover:bg-indigo-50/50 dark:hover:bg-indigo-950/10"
										}`}
									>
										<input
											type="radio"
											name="reflection-selection"
											value=""
											checked={selectedReflections === null}
											onChange={() => {
												setSelectedReflections(null);
												setReflectionResponses({});
											}}
											className="mt-1 border-2 border-gray-300 text-indigo-600 focus:ring-indigo-500"
										/>

										<span className="text-sm font-medium flex-1">
											No reflection related activity today
										</span>
									</label>
									{reflections.map((reflection) => (
										<label
											key={reflection.id}
											className={`flex items-start space-x-3 p-3 rounded-lg border-2 transition-all cursor-pointer ${
												selectedReflections === reflection.id
													? "border-indigo-500 bg-indigo-50 dark:bg-indigo-950/20"
													: "border-border hover:border-indigo-300 hover:bg-indigo-50/50 dark:hover:bg-indigo-950/10"
											}`}
										>
											<input
												type="radio"
												name="reflection-selection"
												value={reflection.id}
												checked={selectedReflections === reflection.id}
												onChange={() => handleReflectionSelect(reflection.id)}
												className="mt-1 border-2 border-gray-300 text-indigo-600 focus:ring-indigo-500"
											/>

											<span className="text-sm font-medium flex-1">
												{reflection.title}
											</span>
										</label>
									))}
								</div>
							</div>
						)}
					</div>

					{selectedReflections !== null && (
						<ReflectionDetailsSection
							selectedReflectionId={selectedReflections}
							reflections={reflections}
							reflectionResponses={reflectionResponses}
							setReflectionResponses={setReflectionResponses}
						/>
					)}

					<div className="flex flex-col sm:flex-row justify-between gap-3 pt-6 border-t">
						<Button
							variant="outline"
							onClick={onBack}
							className="w-full sm:w-auto"
						>
							<ArrowLeft className="h-4 w-4 mr-2" />
							Back
						</Button>
						<Button
							onClick={onSubmit}
							disabled={submitting || !isFormValid}
							size="lg"
							className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-200"
						>
							{submitting ? "Saving..." : "Complete Check-in"}
							<CheckCircle className="h-5 w-5 ml-2" />
						</Button>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
